import React, { useState, useEffect } from "react";
import { Menu, X, Calendar } from "lucide-react";
import { LOGO_URL, CALENDLY_URL, NAV_LINKS } from "@/lib/site-data";

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-brand-cream/85 border-b border-brand-blue/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src={LOGO_URL}
            alt="The Aligned SOULutions"
            className="h-12 w-12 rounded-xl object-contain bg-brand-blue p-0.5 ring-1 ring-brand-blue/20 group-hover:ring-brand-blue/60 transition-all"
          />
          <span className="hidden sm:block font-serif text-lg leading-tight text-brand-ink">
            The Aligned <span className="italic text-brand-blue">SOULutions</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="font-sans text-sm tracking-wide text-brand-ink/80 hover:text-brand-blue transition-colors relative after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-px after:bg-brand-blue after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-call-btn"
            className="hidden md:inline-flex items-center gap-2 bg-brand-blue text-brand-cream hover:bg-brand-ink transition-colors rounded-full px-6 py-2.5 font-sans font-medium text-sm shadow-sm shadow-brand-blue/20"
          >
            <Calendar className="w-4 h-4" />
            Book a Call
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-brand-ink hover:text-brand-blue transition-colors"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-brand-cream/95 backdrop-blur-xl border-t border-brand-blue/10" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="text-left font-sans text-base text-brand-ink/80 hover:text-brand-blue py-2"
              >
                {l.label}
              </button>
            ))}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-book-call-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-blue text-brand-cream rounded-full px-6 py-3 font-medium text-sm"
            >
              <Calendar className="w-4 h-4" /> Book a Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
